/**
 * Full-stack error rate tracking.
 *
 * Logs application errors to the `error_events` table via Supabase RPC.
 * Fire-and-forget — never throws, falls back to console.warn.
 * Pattern mirrors `logAuditEvent` in security/audit.ts.
 */

// ─── Types ──────────────────────────────────────────────────────────────────

export type ErrorSource = "consumer" | "venue" | "admin" | "edge_function";

export type ErrorCategory =
  | "api_error"
  | "rpc_error"
  | "client_error"
  | "network_error"
  | "unhandled_exception";

export type ErrorSeverity = "warning" | "error" | "critical";

export interface TrackErrorOptions {
  source: ErrorSource;
  category: ErrorCategory;
  severity?: ErrorSeverity;
  message: string;
  error?: Error | unknown;
  metadata?: Record<string, unknown>;
}

// ─── Minimal Supabase interface (avoid importing the full lib) ──────────────

interface MinimalSupabase {
  rpc: (
    fn: string,
    args?: Record<string, unknown>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => PromiseLike<{ error: any }>;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

async function sha256Hex(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function normalizeStack(stack: string): string {
  return stack
    .split("\n")
    .slice(0, 4) // first 3 frames + error line
    .map((line) => line.replace(/:\d+:\d+/g, "")) // strip line:col
    .join("\n");
}

async function computeStackHash(
  error: unknown,
): Promise<string | null> {
  if (!(error instanceof Error) || !error.stack) return null;
  try {
    return await sha256Hex(normalizeStack(error.stack));
  } catch {
    return null;
  }
}

// ─── Public API ─────────────────────────────────────────────────────────────

function getMode(): string | undefined {
  try {
    const meta = Function("return import.meta")() as { env?: { MODE?: string } } | undefined;
    if (meta?.env?.MODE) return meta.env.MODE;
  } catch {
    // import.meta unavailable (e.g. CommonJS); fall through
  }
  const proc = (globalThis as { process?: { env?: { NODE_ENV?: string } } }).process;
  if (proc?.env?.NODE_ENV) {
    return proc.env.NODE_ENV;
  }
  return undefined;
}

/**
 * Track an application error. Fire-and-forget.
 * No-ops outside of production to avoid polluting error_events with dev/test noise.
 */
export async function trackError(
  supabase: MinimalSupabase,
  options: TrackErrorOptions,
): Promise<void> {
  if (getMode() !== "production") return;
  try {
    const stackHash = await computeStackHash(options.error);
    const stack =
      options.error instanceof Error ? options.error.stack : undefined;

    const metadata: Record<string, unknown> = {
      ...options.metadata,
      ...(stack ? { stack } : {}),
    };

    const { error } = await supabase.rpc("log_error_event", {
      p_source: options.source,
      p_category: options.category,
      p_severity: options.severity ?? "error",
      p_message: options.message,
      p_stack_hash: stackHash,
      p_metadata: metadata,
    });

    if (error) {
      console.warn("[error-tracking] Failed to log:", options.message, error);
    }
  } catch (err) {
    console.warn("[error-tracking] Failed to log:", options.message, err);
  }
}

/**
 * Convenience wrapper for edge function errors.
 */
export async function trackEdgeFunctionError(
  supabase: MinimalSupabase,
  functionName: string,
  error: unknown,
  metadata?: Record<string, unknown>,
): Promise<void> {
  const message = error instanceof Error ? error.message : String(error);
  return trackError(supabase, {
    source: "edge_function",
    category: "api_error",
    severity: "error",
    message,
    error,
    metadata: { function_name: functionName, ...metadata },
  });
}
